import { useTranslation, useCompanyCurrency } from '@/composables';
import { formatCurrency } from '@/utils/formatUtils';
import { SaleTransactionStatusEnum } from '@/types/enums/SaleTransactionStatusEnum';
import { SaleTransactionHistoryTypeEnum } from '@/types/enums/SaleTransactionHistoryTypeEnum';
import { PaymentMethodEnum } from '@/types/enums/PaymentMethodEnum';

export function useTransaction() {
    const { t } = useTranslation();
    const { currency: companyCurrency, locale } = useCompanyCurrency();

    const getStatusColor = (status: SaleTransactionStatusEnum): string => {
        const colorMap: Record<SaleTransactionStatusEnum, string> = {
            [SaleTransactionStatusEnum.Pending]: 'warning',
            [SaleTransactionStatusEnum.Processing]: 'info',
            [SaleTransactionStatusEnum.Completed]: 'success',
            [SaleTransactionStatusEnum.Failed]: 'error',
            [SaleTransactionStatusEnum.Cancelled]: 'grey',
        };

        return colorMap[status] || 'grey';
    };

    const getStatusLabel = (status: SaleTransactionStatusEnum): string => {
        const labelMap: Record<SaleTransactionStatusEnum, string> = {
            [SaleTransactionStatusEnum.Pending]: t('transactions.status.pending'),
            [SaleTransactionStatusEnum.Processing]: t('transactions.status.processing'),
            [SaleTransactionStatusEnum.Completed]: t('transactions.status.completed'),
            [SaleTransactionStatusEnum.Failed]: t('transactions.status.failed'),
            [SaleTransactionStatusEnum.Cancelled]: t('transactions.status.cancelled'),
        };

        return labelMap[status] || status;
    };

    const getHistoryTypeLabel = (type: SaleTransactionHistoryTypeEnum): string => {
        const labelMap: Record<SaleTransactionHistoryTypeEnum, string> = {
            [SaleTransactionHistoryTypeEnum.Created]: t('transactions.history.created'),
            [SaleTransactionHistoryTypeEnum.StatusChanged]: t('transactions.history.statusChanged'),
            [SaleTransactionHistoryTypeEnum.PaymentInitiated]: t('transactions.history.paymentInitiated'),
            [SaleTransactionHistoryTypeEnum.PaymentCompleted]: t('transactions.history.paymentCompleted'),
            [SaleTransactionHistoryTypeEnum.PaymentFailed]: t('transactions.history.paymentFailed'),
            [SaleTransactionHistoryTypeEnum.Cancelled]: t('transactions.history.cancelled'),
            [SaleTransactionHistoryTypeEnum.Refunded]: t('transactions.history.refunded'),
            [SaleTransactionHistoryTypeEnum.ProductEpcAdded]: t('transactions.history.productEpcAdded'),
            [SaleTransactionHistoryTypeEnum.ProductEpcRemoved]: t('transactions.history.productEpcRemoved'),
            [SaleTransactionHistoryTypeEnum.ProductAdded]: t('transactions.history.productAdded'),
            [SaleTransactionHistoryTypeEnum.ProductRemoved]: t('transactions.history.productRemoved'),
            [SaleTransactionHistoryTypeEnum.LineDiscountApplied]: t('transactions.history.lineDiscountApplied'),
            [SaleTransactionHistoryTypeEnum.LineDiscountRemoved]: t('transactions.history.lineDiscountRemoved'),
            [SaleTransactionHistoryTypeEnum.EmployeeDiscountApplied]: t('transactions.history.employeeDiscountApplied'),
            [SaleTransactionHistoryTypeEnum.EmployeeDiscountRemoved]: t('transactions.history.employeeDiscountRemoved'),
        };

        return labelMap[type] || type;
    };

    const getHistoryTypeIcon = (type: SaleTransactionHistoryTypeEnum): string => {
        const iconMap: Record<SaleTransactionHistoryTypeEnum, string> = {
            [SaleTransactionHistoryTypeEnum.Created]: 'mdi-check-circle',
            [SaleTransactionHistoryTypeEnum.StatusChanged]: 'mdi-swap-horizontal',
            [SaleTransactionHistoryTypeEnum.PaymentInitiated]: 'mdi-credit-card-outline',
            [SaleTransactionHistoryTypeEnum.PaymentCompleted]: 'mdi-credit-card-check',
            [SaleTransactionHistoryTypeEnum.PaymentFailed]: 'mdi-credit-card-remove',
            [SaleTransactionHistoryTypeEnum.Cancelled]: 'mdi-close-circle',
            [SaleTransactionHistoryTypeEnum.Refunded]: 'mdi-cash-refund',
            [SaleTransactionHistoryTypeEnum.ProductEpcAdded]: 'mdi-tag-plus',
            [SaleTransactionHistoryTypeEnum.ProductEpcRemoved]: 'mdi-tag-minus',
            [SaleTransactionHistoryTypeEnum.ProductAdded]: 'mdi-plus-box',
            [SaleTransactionHistoryTypeEnum.ProductRemoved]: 'mdi-minus-box',
            [SaleTransactionHistoryTypeEnum.LineDiscountApplied]: 'mdi-sale',
            [SaleTransactionHistoryTypeEnum.LineDiscountRemoved]: 'mdi-sale-outline',
            [SaleTransactionHistoryTypeEnum.EmployeeDiscountApplied]: 'mdi-account-cash',
            [SaleTransactionHistoryTypeEnum.EmployeeDiscountRemoved]: 'mdi-account-cash-outline',
        };

        return iconMap[type] || 'mdi-circle';
    };

    const getHistoryIconColor = (type: SaleTransactionHistoryTypeEnum): string => {
        const colorMap: Record<SaleTransactionHistoryTypeEnum, string> = {
            [SaleTransactionHistoryTypeEnum.Created]: 'success',
            [SaleTransactionHistoryTypeEnum.StatusChanged]: 'info',
            [SaleTransactionHistoryTypeEnum.PaymentInitiated]: 'info',
            [SaleTransactionHistoryTypeEnum.PaymentCompleted]: 'success',
            [SaleTransactionHistoryTypeEnum.PaymentFailed]: 'error',
            [SaleTransactionHistoryTypeEnum.Cancelled]: 'error',
            [SaleTransactionHistoryTypeEnum.Refunded]: 'warning',
            [SaleTransactionHistoryTypeEnum.ProductEpcAdded]: 'info',
            [SaleTransactionHistoryTypeEnum.ProductEpcRemoved]: 'warning',
            [SaleTransactionHistoryTypeEnum.ProductAdded]: 'info',
            [SaleTransactionHistoryTypeEnum.ProductRemoved]: 'warning',
            [SaleTransactionHistoryTypeEnum.LineDiscountApplied]: 'info',
            [SaleTransactionHistoryTypeEnum.LineDiscountRemoved]: 'warning',
            [SaleTransactionHistoryTypeEnum.EmployeeDiscountApplied]: 'info',
            [SaleTransactionHistoryTypeEnum.EmployeeDiscountRemoved]: 'warning',
        };

        return colorMap[type] || 'grey';
    };

    const getPaymentMethodLabel = (method: PaymentMethodEnum): string => {
        const labelMap: Record<PaymentMethodEnum, string> = {
            [PaymentMethodEnum.Cash]: t('transactions.paymentMethods.cash'),
            [PaymentMethodEnum.Card]: t('transactions.paymentMethods.card'),
            [PaymentMethodEnum.Online]: t('transactions.paymentMethods.online'),
            [PaymentMethodEnum.Mobile]: t('transactions.paymentMethods.mobile'),
            [PaymentMethodEnum.Voucher]: t('transactions.paymentMethods.voucher'),
        };

        return labelMap[method] || method;
    };

    const getPaymentMethodIcon = (method: PaymentMethodEnum): string => {
        const iconMap: Record<PaymentMethodEnum, string> = {
            [PaymentMethodEnum.Cash]: 'mdi-cash',
            [PaymentMethodEnum.Card]: 'mdi-credit-card-outline',
            [PaymentMethodEnum.Online]: 'mdi-web',
            [PaymentMethodEnum.Mobile]: 'mdi-cellphone-nfc',
            [PaymentMethodEnum.Voucher]: 'mdi-wallet-giftcard',
        };

        return iconMap[method] || 'mdi-cash-multiple';
    };

    const getPaymentMethodColor = (method: PaymentMethodEnum): string => {
        const colorMap: Record<PaymentMethodEnum, string> = {
            [PaymentMethodEnum.Cash]: 'success',
            [PaymentMethodEnum.Card]: 'info',
            [PaymentMethodEnum.Online]: 'primary',
            [PaymentMethodEnum.Mobile]: 'primary',
            [PaymentMethodEnum.Voucher]: 'warning',
        };

        return colorMap[method] || 'grey';
    };

    /**
     * Format a transaction amount. Amounts come from the API as decimal strings
     * and are null on transactions the cash register did not send a breakdown for.
     */
    const formatAmount = (amount?: string | null, currency?: string | null): string => {
        if (amount === null || amount === undefined) {
            return '—';
        }

        const value = Number(amount);

        if (Number.isNaN(value)) {
            return '—';
        }

        return formatCurrency(value, currency ?? companyCurrency.value, locale.value);
    };

    /**
     * What a sale brought in once the money paid back to the customer is taken
     * off. A refund worth more than the new goods leaves `total_amount` at zero
     * and records the difference as a positive `payout_amount`, so such a sale
     * comes out negative. Null when the register sent no amounts at all.
     */
    const getNetAmount = (total?: string | null, payout?: string | null): string | null => {
        const paidOut = Number(payout ?? 0);

        if (!paidOut) {
            return total ?? null;
        }

        return (Number(total ?? 0) - paidOut).toFixed(2);
    };

    /** A refund amount is stored positive; shown with a minus, as money going out. */
    const formatNegativeAmount = (amount?: string | null, currency?: string | null): string => {
        if (!Number(amount)) {
            return formatAmount(amount, currency);
        }

        return formatAmount((-Math.abs(Number(amount))).toFixed(2), currency);
    };

    const isNegativeAmount = (amount?: string | null): boolean => Number(amount) < 0;

    return {
        getStatusColor,
        getStatusLabel,
        getHistoryTypeLabel,
        getHistoryTypeIcon,
        getHistoryIconColor,
        getPaymentMethodLabel,
        getPaymentMethodIcon,
        getPaymentMethodColor,
        formatAmount,
        formatNegativeAmount,
        getNetAmount,
        isNegativeAmount,
    };
}
